import {
  Alert,
  Backdrop,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Icon,
  ListItem,
  ListItemText,
  Snackbar,
} from "@mui/material";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";
// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppConfig from "appConfig";
import { useAppLocation } from "agent_location_context";
import GeoTaggedCamera from "app/components/gps_camera";
import PhotoCaptureBox from "app/components/photo_capture_box";
import { useAwaitDialog } from "app/components/status_dialog";

function DawRetailerVisitPage() {
  const pageTitle = `Display & Win - Log Retailer Visit`;
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();
  const dialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const { applocation, permissionStatus, updateAppLocation } = useAppLocation();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [retailer, setRetailer] = useState({});
  const [products, setProducts] = useState([]);
  const [quantities, setQuantities] = useState({});

  const [visitPhoto, setVisitPhoto] = useState(null);
  const [openCamera, setOpenCamera] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(
          `agents/daw/retailers/${ref}/${applocation?.lat}/${applocation?.lng}/fetch.json`,
        )
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setRetailer(result.data.data.retailer);
            setProducts(result.data.data.products);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  const handleQuantityChange = (ref, value) => {
    setQuantities((prev) => ({
      ...prev,
      [ref]: value,
    }));
  };

  async function submitVisit() {
    for (const product of products) {
      const val = quantities[product.reference];

      if (val === "" || val === undefined || val === null) {
        setSeverity("error");
        setErrMsg(`Please enter quantity for ${product.title}`);
        setOpen(true);
        return;
      }
    }
    if (visitPhoto === null) {
      setSeverity("error");
      setErrMsg("Photo is required.");
      setOpen(true);
      return;
    }

    setShowLoading(true);

    const json = {
      reference: ref,
      lat: applocation?.lat,
      lng: applocation?.lng,
      products: products.map((p) => ({
        reference: p.reference,
        quantity: Number(quantities[p.reference] || 0),
      })),
      photo: visitPhoto.image,
    };

    await axios
      .post("agents/daw/retailers/visits.json", json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await dialog.show(result.data.data.message, "success");
          navigate(`/activations/display-win/retailers/${ref}`);
        } else {
          //   setSeverity("error");
          //   setErrMsg(result.data.data.message);
          //   setOpen(true);
          await dialog.show(result.data.data.message, "error", "Failed");
        }
      })
      .catch(async () => {
        const err = `Unable to complete request. ${
          navigator.onLine
            ? ""
            : "Make sure you have an active internet connection"
        }`;
        setShowLoading(false);
        // setSeverity("error");
        // setErrMsg(err);
        // setOpen(true);
        await dialog.show(err, "error", "Failed");
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <MDBox mt={1} mb={5}>
        {pageLoaded && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="space-between" mb={2}>
                <MDBox>
                  <Button
                    color="dark"
                    size="small"
                    startIcon={<Icon>west</Icon>}
                    onClick={() => {
                      if (window.history.length > 1) {
                        window.history.back();
                      } else {
                        navigate(`/activations/display-win/retailers/${ref}`);
                      }
                    }}
                  >
                    Back
                  </Button>
                </MDBox>
              </MDBox>
              <Card>
                <MDBox p={2}>
                  <MDBox align="center">
                    <MDTypography variant="h6" mb={-1}>
                      {retailer.name}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      Enter displayed quantity for each product
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <Divider />
                  </MDBox>

                  <MDBox mt={3}>
                    {products.map((product) => (
                      <MDBox mb={3}>
                        <ListItem key={product.reference}>
                          <ListItemText
                            primary={
                              <MDTypography fontSize={14}>
                                {product.title}
                              </MDTypography>
                            }
                          />

                          <MDInput
                            label="Quantity"
                            type="number"
                            size="small"
                            value={quantities[product.reference] || null}
                            onChange={(e) =>
                              handleQuantityChange(
                                product.reference,
                                e.target.value,
                              )
                            }
                            inputProps={{ min: 0 }}
                            style={{ width: 100 }}
                          />
                        </ListItem>
                      </MDBox>
                    ))}
                  </MDBox>
                  <MDBox>
                    <Divider />
                  </MDBox>
                  <MDBox mt={5} align="center">
                    <GeoTaggedCamera
                      open={openCamera}
                      onClose={() => setOpenCamera(false)}
                      onCapture={(data) => {
                        setVisitPhoto(data);
                        setOpenCamera(false);
                      }}
                    />
                    <MDTypography variant="caption">
                      Take a picture of the products on display.
                    </MDTypography>
                  </MDBox>

                  <MDBox mt={10} mb={3}>
                    <MDButton
                      variant="contained"
                      color="primary"
                      onClick={submitVisit}
                      fullWidth
                    >
                      Submit Visit
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DawRetailerVisitPage;
